/**
 * ics.js — client-side iCalendar (.ics, RFC 5545) generation for the calendar
 * page (docs/FRONTEND-PLAN.md §4.1, FEA-27).
 *
 * WHY THIS EXISTS
 * ---------------
 * There is no backend export endpoint and none is needed: the calendar page
 * already holds everything an export needs — schedule masters, their
 * recurrence rules, the expanded occurrences and the member events. `.ics` is
 * the one format every local/mobile calendar imports (Apple, Google, Outlook,
 * Android), so the whole feature is a string builder plus a `Blob` download.
 *
 * Everything here except `downloadIcs` is pure — no React, no fetch, no
 * ambient clock (`DTSTAMP` arrives as a parameter) — so it is unit-testable in
 * isolation (`ics.test.js`, `cd frontend && npx vitest run`). `downloadIcs` is
 * the deliberate DOM boundary and is the only untested export.
 *
 * TIME ZONES (deliberate, matching lib/recurrence.js)
 * --------------------------------------------------
 * Every DATE-TIME is written as a UTC instant (`…Z`), never with `TZID` and
 * never with a `VTIMEZONE` block. The backend stores plain instants and
 * carries `timezone` as an informational IANA string only; emitting `TZID`
 * would claim a wall-clock fidelity the stored data does not have. The DST
 * caveat documented in the head of recurrence.js applies identically to an
 * exported series: a 09:00-local weekly meeting exported as a UTC RRULE drifts
 * by an hour across a DST boundary, exactly as it already renders in the grid.
 * Exporting what we actually store is the honest choice for v1.
 *
 * WHAT IS NOT HERE
 * ----------------
 * No `VTIMEZONE`/`TZID` (above), no subscription feed (`webcal:`) — that needs
 * a server-hosted, token-authenticated URL — and no `METHOD:REQUEST`
 * invitations: this is an export of what the user can see, not a scheduling
 * transaction.
 */

import { WEEKDAY_NAMES } from './recurrence';

/** RFC 5545 `BYDAY` codes, indexed like `WEEKDAY_NAMES` / `Date#getUTCDay()`. */
const ICS_WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'];

/** Product identifier written into every calendar we generate. */
export const PRODID = '-//Disruption Consulting//Project-CAP//EN';

/**
 * Maximum octets per physical content line (RFC 5545 §3.1). The limit counts
 * OCTETS, not characters, and the CRLF itself does not count towards it.
 */
const MAX_LINE_OCTETS = 75;

const GOOGLE_CALENDAR_BASE = 'https://calendar.google.com/calendar/render';

/* ------------------------------------------------------------------ values */

function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  if (typeof value !== 'string' || !value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function positiveInt(value) {
  const n = Math.floor(Number(value));
  return Number.isFinite(n) && n >= 1 ? n : null;
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Format an instant as an RFC 5545 UTC DATE-TIME: `YYYYMMDDTHHMMSSZ`.
 * Sub-second precision is dropped — the format has no place for it.
 *
 * @param {Date|string|number} value  Anything `new Date()` understands.
 * @returns {string|null}  The formatted value, or null when `value` is not a
 *   usable instant (callers omit the property rather than emit garbage).
 */
export function toIcsDateUtc(value) {
  const date = toDate(value);
  if (!date) return null;
  return (
    `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}` +
    `T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(date.getUTCSeconds())}Z`
  );
}

/**
 * Escape a string for the RFC 5545 TEXT value type (§3.3.11): backslash,
 * semicolon and comma are backslash-escaped and every newline becomes the
 * two-character literal `\n`. Backslash is escaped FIRST so the escapes this
 * function adds are not themselves re-escaped. A lone CR and a CRLF pair both
 * collapse to a single `\n`, because a bare CR has no meaning in a TEXT value.
 *
 * @param {string} value
 * @returns {string}
 */
export function escapeIcsText(value) {
  if (value == null) return '';
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

/** UTF-8 length in octets of a single code point. */
function codePointOctets(codePoint) {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  if (codePoint < 0x10000) return 3;
  return 4;
}

/**
 * Fold one logical content line into physical lines of at most 75 OCTETS
 * (RFC 5545 §3.1). Continuation lines are prefixed with a single space, which
 * counts towards their own 75-octet budget; an unfolding parser strips it.
 *
 * Folding is UTF-8 byte-aware and splits only between code points — a
 * multi-octet character (accented letter, emoji) is never cut in half, which a
 * naive `slice(0, 75)` on JS UTF-16 units would do routinely. Surrogate pairs
 * are handled because iteration is over code points, not units.
 *
 * CRLF is NOT added here: the caller joins the returned segments (see
 * `buildIcsCalendar`), which keeps this function a pure string splitter.
 *
 * @param {string} line  One unfolded content line, e.g. `SUMMARY:Board call`.
 * @returns {string[]}  One or more physical lines, continuation lines already
 *   carrying their leading space. Never empty.
 */
export function foldIcsLine(line) {
  const text = line == null ? '' : String(line);
  const out = [];
  let current = '';
  let octets = 0;
  // The first physical line gets the full budget; every continuation spends
  // one octet on its leading space.
  let budget = MAX_LINE_OCTETS;

  for (const char of text) {
    const size = codePointOctets(char.codePointAt(0));
    if (octets + size > budget) {
      out.push(current);
      current = ' ';
      octets = 1;
      budget = MAX_LINE_OCTETS;
    }
    current += char;
    octets += size;
  }

  out.push(current);
  return out;
}

/* ------------------------------------------------------------------- RRULE */

/**
 * Translate a schedule's `RecurrenceRule` into an RFC 5545 RRULE value (the
 * part after `RRULE:`), mirroring the locked semantics of lib/recurrence.js so
 * an exported series expands in the user's calendar the way it renders here.
 *
 * Mapping — each line is a decision recurrence.js already made:
 *   • `frequency`      → `FREQ` (`daily|weekly|monthly|yearly`, case-folded on
 *                        input). Anything else means "not recurring" there, so
 *                        it returns null here.
 *   • `interval` > 1   → `INTERVAL` (1 is the RRULE default, so it is omitted).
 *   • `daysOfWeek`     → `BYDAY` (weekly only). Names are taken in the order
 *                        given, de-duplicated, unrecognised names dropped. An
 *                        empty/absent list omits BYDAY entirely — DTSTART's own
 *                        weekday then governs, which is exactly what
 *                        recurrence.js does by defaulting to the master's day.
 *   • `dayOfMonth`     → `BYMONTHDAY` (monthly only, 1–31). Out-of-range or
 *                        missing omits it and DTSTART's day-of-month governs.
 *                        RRULE's BYMONTHDAY skips months that lack the day
 *                        (no 31st in April) — the same skip-never-clamp rule as
 *                        decision 2 in recurrence.js.
 *   • yearly           → `FREQ=YEARLY` alone; DTSTART's month/day governs, and
 *                        a Feb-29 series skipping non-leap years matches
 *                        decision 3.
 *   • `weekOfMonth` / `monthOfYear` are IGNORED, for parity with
 *     recurrence.js — no UI writes them and no backend code reads them.
 *
 * Termination: `occurrenceCount` → `COUNT`; otherwise `endDate` → `UNTIL`, at
 * `T235959Z` on that UTC calendar date because the app treats `endDate` as an
 * inclusive by-UTC-date cutoff (decision 4 in recurrence.js — the form writes a
 * bare `<input type="date">` as UTC midnight, so an instant comparison would
 * drop every same-day occurrence).
 *
 * KNOWN DIVERGENCE: RFC 5545 forbids COUNT and UNTIL in the same rule, while
 * recurrence.js decision 5 says that a record carrying both stops at whichever
 * comes first. There is no RRULE that expresses "first of the two", so COUNT
 * wins here. The UI makes the two mutually exclusive, so this only bites a
 * hand-written record — and when it does, the export can run past the
 * `endDate` the grid would have honoured.
 *
 * @param {object|null|undefined} recurrence  A wire `RecurrenceRule`.
 * @returns {string|null}  e.g. `FREQ=WEEKLY;INTERVAL=2;BYDAY=TU,TH;COUNT=10`,
 *   or null when the rule is absent/unrecognised (export it as a single event).
 */
export function scheduleRrule(recurrence) {
  if (!recurrence || typeof recurrence !== 'object') return null;

  const frequency = String(recurrence.frequency || '').toLowerCase();
  if (!FREQUENCIES.includes(frequency)) return null;

  const parts = [`FREQ=${frequency.toUpperCase()}`];

  const interval = positiveInt(recurrence.interval);
  if (interval && interval > 1) parts.push(`INTERVAL=${interval}`);

  if (frequency === 'weekly') {
    const days = Array.isArray(recurrence.daysOfWeek) ? recurrence.daysOfWeek : [];
    const codes = [
      ...new Set(
        days
          .map((day) => WEEKDAY_NAMES.indexOf(String(day || '').toLowerCase()))
          .filter((index) => index >= 0)
          .map((index) => ICS_WEEKDAYS[index])
      )
    ];
    if (codes.length) parts.push(`BYDAY=${codes.join(',')}`);
  }

  if (frequency === 'monthly') {
    const dayOfMonth = positiveInt(recurrence.dayOfMonth);
    if (dayOfMonth && dayOfMonth <= 31) parts.push(`BYMONTHDAY=${dayOfMonth}`);
  }

  const count = positiveInt(recurrence.occurrenceCount);
  if (count) {
    parts.push(`COUNT=${count}`);
  } else {
    const endDate = toDate(recurrence.endDate);
    if (endDate) {
      const until =
        `${endDate.getUTCFullYear()}${pad2(endDate.getUTCMonth() + 1)}` +
        `${pad2(endDate.getUTCDate())}T235959Z`;
      parts.push(`UNTIL=${until}`);
    }
  }

  return parts.join(';');
}

/* ------------------------------------------------------------------ VEVENT */

function textLine(name, value) {
  const escaped = escapeIcsText(value);
  return escaped ? `${name}:${escaped}` : null;
}

/**
 * Build the lines of one VEVENT, `BEGIN:VEVENT`/`END:VEVENT` included and
 * UNFOLDED — `buildIcsCalendar` folds and joins them.
 *
 * Omission rules: `SUMMARY`, `DESCRIPTION`, `LOCATION` and `URL` are dropped
 * when empty rather than written blank. `DTEND` is dropped unless it is
 * strictly after `DTSTART`, because RFC 5545 §3.6.1 requires that ordering and
 * defines an absent DTEND on a DATE-TIME start as a zero-duration event — which
 * is exactly what a member event with no `endsAt` is.
 *
 * A VEVENT is invalid without `UID` and `DTSTART`, so a call missing either
 * returns an EMPTY array: a skipped event beats a corrupt calendar that the
 * user's calendar app refuses wholesale.
 *
 * @param {object} params
 * @param {string} params.uid          Stable, globally unique identifier.
 * @param {string} [params.title]      → SUMMARY.
 * @param {string} [params.description]→ DESCRIPTION.
 * @param {string} [params.location]   → LOCATION.
 * @param {Date|string|number} params.start  → DTSTART (UTC).
 * @param {Date|string|number} [params.end]  → DTEND (UTC).
 * @param {string} [params.url]        → URL (a URI value: NOT text-escaped).
 * @param {string} [params.rrule]      → RRULE value from `scheduleRrule`.
 * @param {Date|string|number} [params.stampDate]  → DTSTAMP. Passed in rather
 *   than read from the clock so this module stays pure; when absent it falls
 *   back to DTSTART, which keeps the required property present and the output
 *   deterministic.
 * @returns {string[]}  VEVENT lines, or `[]` when uid/start are unusable.
 */
export function buildVevent({
  uid,
  title,
  description,
  location,
  start,
  end,
  url,
  rrule,
  stampDate
} = {}) {
  const dtStart = toIcsDateUtc(start);
  if (!uid || !dtStart) return [];

  const dtEnd = toIcsDateUtc(end);
  const startMs = toDate(start).getTime();
  const endDate = toDate(end);

  const lines = ['BEGIN:VEVENT', `UID:${escapeIcsText(uid)}`];
  lines.push(`DTSTAMP:${toIcsDateUtc(stampDate) || dtStart}`);
  lines.push(`DTSTART:${dtStart}`);
  if (dtEnd && endDate && endDate.getTime() > startMs) lines.push(`DTEND:${dtEnd}`);

  [
    textLine('SUMMARY', title),
    textLine('DESCRIPTION', description),
    textLine('LOCATION', location)
  ].forEach((line) => {
    if (line) lines.push(line);
  });

  // URL is a URI value type (§3.3.13) — commas and semicolons are legal URI
  // characters and must NOT be backslash-escaped the way TEXT is.
  if (url) lines.push(`URL:${url}`);
  if (rrule) lines.push(`RRULE:${rrule}`);

  lines.push('END:VEVENT');
  return lines;
}

/**
 * Wrap VEVENTs in a VCALENDAR and serialise: every line folded to 75 octets,
 * joined with CRLF, and terminated with a trailing CRLF (RFC 5545 requires
 * CRLF line endings and a final one).
 *
 * @param {string[][]} veventLineArrays  Output of `buildVevent`, one array per
 *   event. Empty arrays (skipped events) contribute nothing.
 * @returns {string}  A complete `.ics` document.
 */
export function buildIcsCalendar(veventLineArrays) {
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', `PRODID:${PRODID}`, 'CALSCALE:GREGORIAN'];
  (veventLineArrays || []).forEach((vevent) => {
    (vevent || []).forEach((line) => lines.push(line));
  });
  lines.push('END:VCALENDAR');

  const folded = [];
  lines.forEach((line) => {
    foldIcsLine(line).forEach((physical) => folded.push(physical));
  });
  return `${folded.join('\r\n')}\r\n`;
}

/* ------------------------------------------------------------------ google */

/**
 * Build a Google Calendar "template" URL — the pre-filled event-creation form.
 * Offered next to the .ics download for a SINGLE occurrence only: the template
 * endpoint has no documented, stable way to express a recurrence rule, and a
 * link that silently dropped the repeat would be worse than no link.
 *
 * Times use the same compact UTC form as the .ics (`YYYYMMDDTHHMMSSZ`); an
 * event with no usable end reuses its start, which Google renders as a
 * zero-length event the user can drag out.
 *
 * @param {object} params
 * @param {string} [params.title]
 * @param {Date|string|number} params.start
 * @param {Date|string|number} [params.end]
 * @param {string} [params.description]
 * @param {string} [params.location]
 * @returns {string|null}  The URL, or null when `start` is unusable.
 */
export function googleCalendarUrl({ title, start, end, description, location } = {}) {
  const dtStart = toIcsDateUtc(start);
  if (!dtStart) return null;
  const dtEnd = toIcsDateUtc(end) || dtStart;

  const query = ['action=TEMPLATE'];
  if (title) query.push(`text=${encodeURIComponent(title)}`);
  // The `/` separating the two instants is Google's syntax, not data — the
  // instants themselves are encoded (a no-op on this alphabet, but the rule
  // holds if the format ever widens).
  query.push(`dates=${encodeURIComponent(dtStart)}/${encodeURIComponent(dtEnd)}`);
  if (description) query.push(`details=${encodeURIComponent(description)}`);
  if (location) query.push(`location=${encodeURIComponent(location)}`);

  return `${GOOGLE_CALENDAR_BASE}?${query.join('&')}`;
}

/* --------------------------------------------------------------- filenames */

/**
 * Slugify a subject/name into a safe filename stem: lowercase, non-alphanumeric
 * runs collapsed to single dashes, trimmed, capped at 60 characters so no
 * filesystem or download bar chokes on a 300-character subject.
 *
 * @param {string} value
 * @param {string} [fallback='entry']  Used when nothing survives slugification
 *   (an emoji-only or empty subject).
 * @returns {string}
 */
export function slugify(value, fallback = 'entry') {
  const slug = String(value == null ? '' : value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
  return slug || fallback;
}

/* ---------------------------------------------------------------- download */

/**
 * Hand an .ics string to the browser as a download. This is the module's only
 * DOM-touching export — the deliberate boundary between the pure builders and
 * the page — and therefore the only one not unit-tested.
 *
 * `text/calendar` is what makes this work on phones: iOS and Android hand the
 * file straight to the calendar app's import sheet instead of showing text.
 * The object URL is revoked immediately after the synthetic click; the browser
 * has already snapshotted the blob by then.
 *
 * @param {string} filename  e.g. `board-call.ics`.
 * @param {string} icsString  Output of `buildIcsCalendar`.
 * @returns {boolean}  false when there is no DOM to download into.
 */
export function downloadIcs(filename, icsString) {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
    return false;
  }
  const blob = new Blob([icsString], { type: 'text/calendar;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);
  return true;
}
